'use client';

import React, { useEffect, useState } from 'react';
import FareSummary from '@/components/fare-summary/fare-summary';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import BookingSummary from '@/components/booking-summary/summary';
import DatePicker from '@/components/picker/DatePicker';
import { addYears, formatDate, subYears, isValid } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from '@/lib/navigation';
import {
  useBookTicketMutation,
  useRegisterDropdownMutation,
} from '@flysync/shared-redux/services/booking';
import { useToast } from '@/hooks/use-toast';

import {
  updatePassenger,
  updateReviewData,
  updateGst,
  updateFareDetails,
  updateSsrInfo,
} from '@flysync/shared-redux/slices/flightSearchSlice';
// import SearchableDropdown from '@/components/ui/SearchableDropdown';
import Combobox from '@/components/ui/combobox';
import { ChevronDown, Search, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useRef } from 'react';
import { config } from '@/config';
import { title } from 'process';
// type TabTypes = 'personal' | 'business';

interface Passenger {
  travelWithData?: {
    title: string;
    firstName: string;
    lastName: string;
  };
  type?: string;
  title: string;
  firstName: string;
  lastName: string;
  dob: string;
  idNumber: string;
  travelWith: string;
  travel_withId?: number;
  passport_no: string;
  issue_date: string;
  nationality: string;
  nationalityId: string;
  expiry_date: string;
  issued_country: string;
  issued_countryId: string;
  assisted_by?: string;
  assisted_byId?: string;
  ssrInfo: SSR[][];
  pax_type?: string;
  travelMode?: string;
  // Individual SSR amounts for this passenger
  seatAmount?: number;
  mealAmount?: number;
  baggageAmount?: number;
  totalSSRAmount?: number;
  // Individual SSR arrays at passenger level
  seat: SSR[];
  meal: SSR[];
  baggage: SSR[];
  sector?: string;
  save_passenger?: string;
}

interface SSR {
  code?: string;
  amount: number;
  desc?: string;
  value?: string;
  origin?: string;
  destination?: string;
  Weight?: number;
  wayType?: number;
  description?: number;
  airlineCode?: string;
  flightNumber?: string;
  id?: string;
  quantity?: number;
  active?: boolean;
  count?: number;
}

interface Seat {
  id: string;
  code: string;
  amount: number;
  desc: string;
  value: string;
}

interface Meal {
  id: string;
  code: string;
  amount: number;
  desc: string;
  value: string;
}

interface Baggage {
  id: string;
  code: string;
  amount: number;
  desc: string;
  value: string;
}

interface SSR {
  seat: Seat[];
  meal: Meal[];
  baggage: Baggage[];
}

function TravelerDetails() {
  const dispatch = useDispatch();

  const { toast } = useToast();

  const router = useRouter();

  const [nationalityOptions, setNationalityOptions] = React.useState<any[]>([]);

  const [issueCountryOptions, setIssueCountryOptions] = React.useState<any[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState('');

  let flightSearchData = useSelector((state: any) => state.flightSearch);

  const stateData: any = useSelector((state: any) => state.flightSearch);

  const userInput = useSelector((state: any) => state.userInputs);
  const { selectedBucket } = userInput;

  const isGstMandatory = flightSearchData?.selectBucket[0]?.isGstMandatory;

  const isGST =
    flightSearchData?.fareRules[0]?.airline_code === '6E' &&
    flightSearchData?.fareRules[0]?.fareTypeId?.toLowerCase()?.includes('sme');
  const [registerDropdown] = useRegisterDropdownMutation();

  const [gstDetails, updateGstDetailsState] = React.useState({
    register_no: '',
    register_company_name: '',
    register_email: '',
    register_address: '',
    register_mobile: '',
  });

  const [contactDetails, setContactDetails] = React.useState({
    country: '',
    mobile_no: '',
    email_id: '',
  });

  const [assistedByOptions, setAssistedByOptions] = React.useState<any[]>([]);

  const [travellerDetails, updateTravellerDetailsState] = React.useState<any[]>(
    [],
  );

  const [errors, setErrors] = React.useState<any[]>([]);

  const hasRun = useRef(false);

  const [isApiCall, setIsApiCall] = React.useState<boolean>(true);

  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [currentPassengerIndex, setCurrentPassengerIndex] = useState(0);

  const [showCompanyPopup, setShowCompanyPopup] = useState(false);

  const [book, { isLoading, error: bookError }] = useBookTicketMutation({
    fixedCacheKey: 'book-ticket',
  });

  const flightSearch = useSelector((state: any) => state.flightSearch);


  // toast({
  //   title: 'Scheduled: Catch up',
  //   description: 'Friday, February 10, 2023 at 5:57 PM',
  // });

  // const [openCalendarIndex, setOpenCalendarIndex] = React.useState<number | null>(null);

  // Define the reference type properly
  type InputRefType = {
    [key: string]: React.RefObject<HTMLInputElement>;
  };

  // Add refs for the inputs
  const inputRefs = React.useRef<InputRefType[]>([]);

  // Initialize refs for each traveler's inputs
  React.useEffect(() => {
    // Create new refs array for each traveler
    inputRefs.current = travellerDetails.map(() => {
      return {
        title: React.createRef<HTMLInputElement>(),
        firstName: React.createRef<HTMLInputElement>(),
        lastName: React.createRef<HTMLInputElement>(),
        dob: React.createRef<HTMLInputElement>(),
        idNumber: React.createRef<HTMLInputElement>(),
        nationality: React.createRef<HTMLInputElement>(),
        nationalityId: React.createRef<HTMLInputElement>(),
        passport_no: React.createRef<HTMLInputElement>(),
        issued_country: React.createRef<HTMLInputElement>(),
        issued_countryId: React.createRef<HTMLInputElement>(),
        issue_date: React.createRef<HTMLInputElement>(),
        expiry_date: React.createRef<HTMLInputElement>(),
        travelWith: React.createRef<HTMLInputElement>(),
        mobile_no: React.createRef<HTMLInputElement>(),
        email_id: React.createRef<HTMLInputElement>(),
        register_company_name: React.createRef<HTMLInputElement>(),
        register_no: React.createRef<HTMLInputElement>(),
        register_email: React.createRef<HTMLInputElement>(),
        register_address: React.createRef<HTMLInputElement>(),
        register_mobile: React.createRef<HTMLInputElement>(),
      };
    });

    if (isApiCall) {
      handleApiCall();
      setIsApiCall(false);
    }
  }, [travellerDetails.length]);

  useEffect(() => {
    if (isGST && isGstMandatory) {
      updateGstDetailsState({
        register_no: config?.registerNo || '',
        register_email: config?.registerEmail || '',
        register_mobile: config?.registerMobile || '',
        register_company_name: config?.registerCompanyName || '',
        register_address: config?.registerAddress || '',
      });
    }
  }, [isGST, isGstMandatory, config]);

  // Initialize GST details from Redux state if they exist
  useEffect(() => {
    if (
      stateData?.gst &&
      Object.values(stateData.gst).some(
        (value) => value && value.toString().trim() !== '',
      )
    ) {
      updateGstDetailsState({
        register_no: stateData.gst.register_no || '',
        register_email: stateData.gst.register_email || '',
        register_mobile: stateData.gst.register_mobile || '',
        register_company_name: stateData.gst.register_company_name || '',
        register_address: stateData.gst.register_address || '',
      });
    }
  }, [stateData?.gst]);

  const handleApiCall = async () => {
    if (hasRun.current) return;
    hasRun.current = true;
    const { data, priceIds, fareRules, fare_details, trip_type } = flightSearch;
    const params = { data, priceIds, fareRules, fare_details, trip_type };
    setIsApiCall(false);
    const responseData: any = await book({
      data: params,
    }).unwrap();

    if (bookError) {
      toast({
        title: 'Error',
        description: 'Something went wrong',
      });
      router.push('/flight-list');
      return;
    }

    if (responseData) {
      const ssr: Array<any> = responseData?.reviewData?.ssrInfo;
      ssr.map((item) => {
        item.map((data: any) => {
          // Create new filtered array instead of modifying original
          const filteredMeals = data.meal.filter(
            (meal: any) => meal.desc !== '',
          );
          // Create new object with updated meal array
          return { ...data, meal: filteredMeals };
        });
      });
      dispatch(updateSsrInfo(ssr?.length > 0 ? [...ssr] : []));
      const flightData = flightSearch.data.map((item: any, index: number) => ({
        ...item,
        price_details: {
          ...item.price_details,
          otherInfo: responseData?.reviewData?.bookingId[index]?.otherInfo,
        },
      }));
      dispatch(
        updateReviewData({
          api_booking_id: responseData?.reviewData?.bookingId,
          data: flightData,
        }),
      );
    }
  };
  // Handle tab key navigation
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentField: string,
    index: number = -1,
  ) => {
    const gstFields = [
      'register_company_name',
      'register_no',
      'register_email',
      'register_address',
      'register_mobile',
    ];
    const contactAndGstFields = ['mobile_no', 'email_id', ...gstFields];
    const getPassengerFields = (travellerIndex: number) => {
      const traveller = travellerDetails[travellerIndex];
      const fields = ['title', 'firstName', 'lastName'];
      const showDob =
        traveller?.pax_type != 'ADT' ||
        flightSearch?.travelMode == 'IN' ||
        stateData?.fareType[0] == 'Senior_Citizen' ||
        stateData?.fareType[0] == 'Student';
      const showIdNumber =
        stateData?.fareType[0] == 'Student' ||
        stateData?.fareType[0] == 'Senior_Citizen' ||
        stateData?.fareType[0] == 'Armed_Forces' ||
        stateData?.fareType[0] == 'Doctors_Nurses';

      if (showDob) fields.push('dob');
      if (showIdNumber) fields.push('idNumber');
      if (traveller?.pax_type === 'INF') fields.push('travelWith');
      if (stateData.travelMode === 'IN') {
        fields.push(
          'nationalityId',
          'passport_no',
          'issued_countryId',
          'issue_date',
          'expiry_date',
        );
      }

      return fields;
    };
    const focusField = (field: string, travellerIndex: number = 0) => {
      const target = inputRefs.current[travellerIndex]?.[field]?.current;
      if (!target) return false;

      target.focus();
      if (
        field === 'dob' ||
        field === 'issue_date' ||
        field === 'expiry_date'
      ) {
        target.click();
      }

      return true;
    };

    if (e.key === 'Tab' && !e.shiftKey) {
      const fields =
        index === -1 ? contactAndGstFields : getPassengerFields(index);
      const currentIndex = fields.indexOf(currentField);
      if (currentIndex === -1) return;

      let nextField = fields[currentIndex + 1];
      let nextIndex = index;

      if (!nextField && index > -1) {
        if (index < travellerDetails.length - 1) {
          nextIndex = index + 1;
          nextField = getPassengerFields(nextIndex)[0];
        } else {
          nextIndex = 0;
          nextField = contactAndGstFields[0];
        }
      }

      if (
        nextField &&
        focusField(nextField, nextIndex === -1 ? 0 : nextIndex)
      ) {
        e.preventDefault();
      }
    }
  };

  const getContinueErrors = (
    passengerData: Passenger[],
    contactData: typeof contactDetails,
    gstData: typeof gstDetails,
  ) => {
    const newErrors: any[] = passengerData.map(() => ({}));
    let hasErrors = false;
    const isDomestic = stateData?.travelMode === 'DO';
    const isInternational = stateData?.travelMode === 'IN';
    const isStudent = stateData?.fareType?.[0] === 'Student';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const addError = (index: number, field: string, message: string) => {
      if (!newErrors[index]) newErrors[index] = {};
      newErrors[index][field] = message;
      hasErrors = true;
    };
    const isEmpty = (value?: string) => !value || value.trim() === '';

    passengerData.forEach((passenger, index) => {
      if (isEmpty(passenger.firstName)) {
        addError(index, 'firstName', 'First Name is required');
      }
      if (isEmpty(passenger.lastName)) {
        addError(index, 'lastName', 'Last Name is required');
      }
      if (
        isDomestic &&
        (passenger.pax_type === 'CHD' || passenger.pax_type === 'INF') &&
        isEmpty(passenger.dob)
      ) {
        addError(
          index,
          'dob',
          `Date of Birth is mandatory for ${passenger.pax_type === 'CHD' ? 'child' : 'infant'} passengers`,
        );
      }
      if (isInternational) {
        if (isEmpty(passenger.dob)) {
          addError(index, 'dob', 'Date of Birth is mandatory for international flights');
        }
        if (isEmpty(passenger.nationality)) {
          addError(index, 'nationality', 'Nationality is required');
        }
        if (isEmpty(passenger.passport_no)) {
          addError(index, 'passport_no', 'Passport Number is required');
        }
        if (isEmpty(passenger.issued_country)) {
          addError(index, 'issued_country', 'Issue Country is required');
        }
        if (isEmpty(passenger.issue_date)) {
          addError(index, 'issue_date', 'Passport Issue Date is required');
        }
        if (isEmpty(passenger.expiry_date)) {
          addError(index, 'expiry_date', 'Passport Expiry is required');
        }
      }
      if (passenger.pax_type === 'INF' && isEmpty(passenger.travelWith)) {
        addError(index, 'travelWith', 'Assisted By is mandatory for infant passengers');
      }
      if (isStudent && isEmpty(passenger.idNumber)) {
        addError(index, 'idNumber', 'Student ID Number is mandatory');
      }
    });

    if (!/^\d{10}$/.test(contactData.mobile_no || '')) {
      addError(0, 'mobile_no', 'Please enter a valid mobile number');
    }
    if (isEmpty(contactData.email_id)) {
      addError(0, 'email_id', 'Email is required');
    } else if (!emailRegex.test(contactData.email_id)) {
      addError(0, 'email_id', 'Invalid Email');
    }

    if (isGST && isGstMandatory) {
      if (isEmpty(gstData.register_company_name)) {
        addError(0, 'register_company_name', 'Company Name is required');
      }
      if (!gstData.register_no || gstData.register_no.length !== 15) {
        addError(0, 'register_no', 'GST Number must be 15 characters');
      }
      if (isEmpty(gstData.register_email)) {
        addError(0, 'register_email', 'Registration Email is required');
      } else if (!emailRegex.test(gstData.register_email)) {
        addError(0, 'register_email', 'Invalid Email');
      }
      if (isEmpty(gstData.register_address)) {
        addError(0, 'register_address', 'Registration Address is required');
      }
      if (!/^\d{10}$/.test(gstData.register_mobile || '')) {
        addError(0, 'register_mobile', 'Please enter a valid mobile number');
      }
    }

    return { hasErrors, errors: newErrors };
  };

  const callback = () => {
    const passengerData: Passenger[] = travellerDetails.map((detail) => ({
      title: detail?.title || 'Mr',
      type: detail.type,
      firstName: (detail.firstName || '').toUpperCase(),
      lastName: (detail.lastName || '').toUpperCase(),
      dob: detail.dob,
      idNumber: detail.idNumber,
      nationality: detail.nationality,
      nationalityId: detail.nationalityId,
      passport_no: detail.passport_no,
      issue_date: detail.issue_date,
      expiry_date: detail.expiry_date,
      issued_country: detail.issued_country,
      issued_countryId: detail.issued_countryId,
      pax_type: detail.pax_type,
      travelWith: detail.travelWith,
      travel_with: detail.travelWith,
      travel_withId: detail.travelWithId,
      ssrInfo: [],
      seatAmount: 0,
      mealAmount: 0,
      baggageAmount: 0,
      totalSSRAmount: 0,
      seat: [],
      meal: [],
      baggage: [],
      sector: '',
      save_passenger: 'No',
    }));

    passengerData.map((data: Passenger, index: number) => {
      if (data.pax_type === 'INF') {
        const adultData =
          passengerData[data?.travel_withId ? data?.travel_withId - 1 : 0];
        passengerData[index].travelWithData = {
          title: adultData.title,
          firstName: adultData.firstName,
          lastName: adultData.lastName,
        };
      }
    });

    const validation = getContinueErrors(
      passengerData,
      contactDetails,
      gstDetails,
    );

    if (validation.hasErrors) {
      setErrors(validation.errors);
      return;
    }

    setErrors([]);
    dispatch(updateReviewData(contactDetails));
    dispatch(updatePassenger(passengerData));
    dispatch(updateGst(gstDetails));
    dispatch(updateFareDetails(flightSearch?.fareDetails));
    router.push('/ssr-detail');
  };
  const getAssistedByOptions = () => {
    const assistedBy = travellerDetails.filter(
      (traveller) => traveller.pax_type === 'ADT',
    );
    const assistedByOptions = assistedBy.map((traveller, index) => ({
      id: index + 1,
      value:
        traveller.title && traveller.firstName && traveller.lastName
          ? `${traveller.title} ${traveller.firstName.toUpperCase()} ${traveller.lastName.toUpperCase()}`
          : traveller.type,
    }));
    setAssistedByOptions(assistedByOptions);
  };

  // Call getAssistedByOptions whenever travellerDetails changes
  React.useEffect(() => {
    getAssistedByOptions();
  }, [travellerDetails]);

  React.useEffect(() => {
    registerDropdown({ data: { state: '' } })
      .unwrap()
      .then((res: any) => {
        setIssueCountryOptions(res.status.data.country);
        setNationalityOptions(res.status.data.country);
      });

    if (stateData.mobile_no) {
      setContactDetails({
        country: stateData.country,
        mobile_no: stateData.mobile_no,
        email_id: stateData.email_id,
      });
    }

    if (stateData?.passenger?.length > 0) {
      updateTravellerDetailsState(stateData.passenger);
    }

    if (stateData?.passengers && stateData?.passenger?.length === 0) {
      const newTravellerDetails: any[] = [];

      for (let i = 0; i < stateData.passengers.adult; i++) {
        newTravellerDetails.push({
          type: `Adult ${i + 1}`,
          firstName: '',
          lastName: '',
          dob: '',
          idNumber: '',
          nationality: '',
          nationalityId: '',
          passport_no: '',
          issue_date: '',
          expiry_date: '',
          issued_country: '',
          issued_countryId: '',
          pax_type: 'ADT',
          title: 'Mr',
        });
      }

      for (let j = 0; j < stateData.passengers.child; j++) {
        newTravellerDetails.push({
          ...newTravellerDetails[0],
          pax_type: 'CHD',
          type: `Child ${j + 1}`,
          title: 'Ms',
        });
      }

      for (let k = 0; k < stateData.passengers.infant; k++) {
        newTravellerDetails.push({
          ...newTravellerDetails[0],
          pax_type: 'INF',
          type: `Infant ${k + 1}`,
          title: 'Ms',
        });
      }

      updateTravellerDetailsState(newTravellerDetails);
    }
  }, [stateData?.passenger, stateData.passengers]);

  const updateTravellerDetails = (
    value: string,
    field: keyof (typeof travellerDetails)[0],
    index: number,
    type: string = 'text',
    id: string = '',
  ) => {
    const updatedDetails = [...travellerDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    if (type === 'dropdown') {
      const fieldWithId = `${field.toString()}Id`;
      updatedDetails[index][fieldWithId] = id;
    }

    updateTravellerDetailsState(updatedDetails);
  };

  const [selectedCountry, setSelectedCountry] = React.useState({
    code: '+91',
    flag: '🇮🇳',
    name: 'India',
  });

  // Add this array of countries either in a separate constants file or within the component
  const countries = [
    { code: '+91', flag: '🇮🇳', name: 'India' },
    // { code: '+1', flag: '🇺🇸', name: 'United States' },
    // { code: '+44', flag: '🇬🇧', name: 'United Kingdom' },
    // { code: '+61', flag: '🇦🇺', name: 'Australia' },
    // { code: '+86', flag: '🇨🇳', name: 'China' },
    // Add more countries as needed
  ];

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery),
  );

  const updateGstDetails = (value: string, field: string) => {
    const updatedDetails: any = { ...gstDetails };
    updatedDetails[field] = value;
    updateGstDetailsState(updatedDetails);
  };

  const updateContactDetails = (value: string, field: string) => {
    const updatedDetails: any = { ...contactDetails };
    updatedDetails[field] = value;
    setContactDetails(updatedDetails);
  };

  const onChangeDate = (dateObj: Date, index: number, field: string) => {
    const formattedDate = formatDate(dateObj, 'dd MMM yyyy');
    updateTravellerDetails(
      formattedDate,
      field as keyof (typeof travellerDetails)[0],
      index,
    );
  };

  const MobilePassengerPopup = React.memo(
    ({
      isOpen,
      onClose,
      index,
    }: {
      isOpen: boolean;
      onClose: () => void;
      index: number;
    }) => {
      const [formData, setFormData] = React.useState({
        title: '',
        firstName: '',
        lastName: '',
        dob: '',
        idNumber: '',
        nationality: '',
        nationalityId: '',
        passport_no: '',
        issue_date: '',
        expiry_date: '',
        issued_country: '',
        issued_countryId: '',
        travelWith: '',
        travelWithId: '',
      });

      const [paxError, setPaxError] = React.useState<any>({});

      // Track if popup was just opened to initialize form data only once
      const wasOpenRef = React.useRef(false);

      // Initialize form data when popup opens (only when transitioning from closed to open)
      React.useEffect(() => {
        if (isOpen && !wasOpenRef.current && travellerDetails[index]) {
          // Popup just opened, initialize form data
          setFormData({
            title:
              travellerDetails[index]?.title ||
              (travellerDetails[index]?.pax_type == 'ADT' ? 'Mr' : 'Mstr'),
            firstName: travellerDetails[index]?.firstName || '',
            lastName: travellerDetails[index]?.lastName || '',
            dob: travellerDetails[index]?.dob || '',
            idNumber: travellerDetails[index]?.idNumber || '',
            nationality: travellerDetails[index]?.nationality || '',
            nationalityId: travellerDetails[index]?.nationalityId || '',
            passport_no: travellerDetails[index]?.passport_no || '',
            issue_date: travellerDetails[index]?.issue_date || '',
            expiry_date: travellerDetails[index]?.expiry_date || '',
            issued_country: travellerDetails[index]?.issued_country || '',
            issued_countryId: travellerDetails[index]?.issued_countryId || '',
            travelWith: travellerDetails[index]?.travelWith || '',
            travelWithId: travellerDetails[index]?.travelWithId || '',
          });
          wasOpenRef.current = true;
        } else if (!isOpen) {
          // Popup closed, reset the ref
          wasOpenRef.current = false;
        }
      }, [isOpen, index]);

      const validateMandatoryFields = (
        formData: any,
        paxType: string,
      ): { isValid: boolean; errors: any } => {
        const errors: any = {};
        const isDomestic = stateData?.travelMode === 'DO';
        const isInternational = stateData?.travelMode === 'IN';
        const isStudent = stateData?.fareType?.[0] === 'Student';

        // Basic validations
        if (!formData.firstName?.trim()) {
          errors.firstName = 'First name is required';
        }
        if (!formData.lastName?.trim()) {
          errors.lastName = 'Last name is required';
        }

        // Child and Infant DOB validation for domestic flights
        if (
          (paxType === 'CHD' || paxType === 'INF') &&
          isDomestic &&
          !formData.dob
        ) {
          errors.dob = `Date of Birth is mandatory for ${paxType === 'CHD' ? 'child' : 'infant'} passengers`;
        }

        // International flight validations
        if (isInternational) {
          const requiredFields = {
            dob: 'Date of Birth',
            nationality: 'Nationality',
            passport_no: 'Passport Number',
            issued_country: 'Issued Country',
            issue_date: 'Issue Date',
            expiry_date: 'Expiry Date',
          };

          Object.entries(requiredFields).forEach(([field, label]) => {
            if (!formData[field]) {
              errors[field] = `${label} is mandatory for international flights`;
            }
          });
        }

        // Infant assisted by validation
        if (paxType === 'INF' && !formData.travelWith) {
          errors.travelWith = 'Assisted By is mandatory for infant passengers';
        }

        // Student ID validation
        if (isStudent && !formData.idNumber) {
          errors.idNumber = 'Student ID Number is mandatory';
        }

        return {
          isValid: Object.keys(errors).length === 0,
          errors,
        };
      };

      const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const paxType = travellerDetails[index]?.pax_type;
        const validation = validateMandatoryFields(formData, paxType);

        if (!validation.isValid) {
          setFormData({
            ...formData,
          });
          // Update errors state
          setPaxError((prevErrors: any) => {
            const newErrors = Array.isArray(prevErrors) ? [...prevErrors] : [];
            if (!newErrors[index]) {
              newErrors[index] = {};
            }
            newErrors[index] = validation.errors;
            return newErrors;
          });

          // Show error toast with specific messages
          // const errorMessages = Object.values(validation.errors);
          // const firstError = errorMessages[0] as string;
          // const remainingCount = errorMessages.length - 1;

          // toast({
          //   title: 'Required Fields Missing',
          //   description:
          //     remainingCount > 0
          //       ? `${firstError} (and ${remainingCount} more field${remainingCount > 1 ? 's' : ''})`
          //       : firstError,
          //   variant: 'destructive',
          // });
          // Don't close popup - keep form data and let user fix errors
          return;
        }

        // Clear errors for this index
        setPaxError((prevErrors: any) => {
          const newErrors = Array.isArray(prevErrors) ? [...prevErrors] : [];
          if (newErrors[index]) {
            newErrors[index] = {};
          }
          return newErrors;
        });

        // Create a copy of the current traveler details
        const updatedDetails = [...travellerDetails];

        // Update the specific traveler's details
        updatedDetails[index] = {
          ...updatedDetails[index],
          ...formData,
        };
        // Update the state with the new details
        updateTravellerDetailsState(updatedDetails);

        // Show success toast
        toast({
          title: 'Success',
          description: 'Passenger details saved successfully',
        });

        // Close popup only after successful validation and save
        onClose();
      };

      const handleDialogChange = (open: boolean) => {
        if (!open) {
          // User is trying to close the dialog
          // Allow closing without validation (e.g., clicking outside or close button)
          onClose();
        }
      };

      return (
        <Dialog open={isOpen} onOpenChange={handleDialogChange}>
          <DialogContent
            className="max-h-[90vh] max-w-[95%] overflow-y-auto rounded-lg p-4 sm:hidden"
            aria-describedby="passenger-form-description"
          >
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {travellerDetails[index]?.type || `Passenger ${index + 1}`}
              </DialogTitle>
              <DialogDescription
                id="passenger-form-description"
                className="text-sm text-gray-500"
              >
                Please fill in the passenger details below
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-4">
              {/* Title Input - Fix Safari auto-open issue */}
              <div className="w-full">
                <Label
                  htmlFor={`title-${index}`}
                  className="mb-2 block text-sm font-medium"
                >
                  Title
                </Label>
                <select
                  id={`title-${index}`}
                  value={formData.title}
                  onInput={(e) => {
                    setFormData({
                      ...formData,
                      title: (e.target as HTMLSelectElement).value,
                    });
                  }}
                  className="border-light h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  style={{ fontSize: '16px' }} // Safari zoom prevention
                  required
                  tabIndex={-1}
                  onFocus={(e) => {
                    // Prevent auto-focus on dialog open in Safari
                    if (!wasOpenRef.current) {
                      e.target.blur();
                    }
                  }}
                >
                  {travellerDetails[index]?.pax_type == 'ADT' ? (
                    <>
                      <option value="Mr">Mr</option>
                      <option value="Ms">Ms</option>
                      <option value="Mrs">Mrs</option>
                    </>
                  ) : (
                    <>
                      <option value="Mstr">Mstr</option>
                      <option value="Miss">Miss</option>
                    </>
                  )}
                </select>
              </div>

              <Input
                placeholder="First Name"
                className="outline-none"
                error={paxError[index]?.firstName}
                required={true}
                width={'w-full'}
                name={`firstName`}
                id={`firstName`}
                value={formData.firstName.toUpperCase()}
                autoFocus={true}
                onInput={(e) => {
                  const currentValue = (e.target as HTMLInputElement).value;
                  setFormData({
                    ...formData,
                    firstName: currentValue.toUpperCase(),
                  });
                }}
              />

              <Input
                placeholder="Last Name"
                className="outline-none"
                required={true}
                width={'w-full'}
                name={`lastName`}
                id={`lastName`}
                value={formData.lastName.toUpperCase()}
                error={paxError[index]?.lastName}
                onInput={(e) => {
                  const currentValue = (e.target as HTMLInputElement).value;
                  setFormData({
                    ...formData,
                    lastName: currentValue.toUpperCase(),
                  });
                }}
              />

              {(travellerDetails[index]?.pax_type != 'ADT' ||
                flightSearch?.travelMode == 'IN' ||
                stateData?.fareType[0] == 'Senior_Citizen' ||
                stateData?.fareType[0] == 'Student') && (
                <DatePicker
                  title="Date of Birth"
                  className="w-full"
                  isChangeMonth={true}
                  setDate={(date) => {
                    const formattedDate = formatDate(date, 'dd MMM yyyy');
                    setFormData((prevData) => ({
                      ...prevData,
                      dob: formattedDate,
                    }));
                  }}
                  selectedDate={
                    formData.dob
                      ? new Date(formData.dob)
                      : new Date(
                          travellerDetails[index]?.pax_type == 'ADT'
                            ? subYears(new Date(flightSearchData.date), 12)
                            : travellerDetails[index]?.pax_type == 'CHD'
                              ? subYears(new Date(flightSearchData.date), 2)
                              : new Date(flightSearchData.date),
                        )
                  }
                  disableDates={{
                    min:
                      travellerDetails[index]?.pax_type == 'ADT'
                        ? subYears(new Date(flightSearchData.date), 100)
                        : travellerDetails[index]?.pax_type == 'CHD'
                          ? subYears(new Date(flightSearchData.date), 12)
                          : subYears(new Date(flightSearchData.date), 2),
                    max:
                      travellerDetails[index]?.pax_type == 'ADT'
                        ? subYears(new Date(flightSearchData.date), 12)
                        : travellerDetails[index]?.pax_type == 'CHD'
                          ? subYears(new Date(flightSearchData.date), 2)
                          : new Date(flightSearchData.date),
                  }}
                  disabled={false}
                >
                  <TriggerComponent
                    error={paxError[index]?.dob}
                    title="Date of Birth"
                    value={formData.dob}
                    name={`dob`}
                    id={`dob`}
                  />
                </DatePicker>
              )}

              {(stateData?.fareType[0] == 'Student' ||
                stateData?.fareType[0] == 'Senior_Citizen' ||
                stateData?.fareType[0] == 'Armed_Forces' ||
                stateData?.fareType[0] == 'Doctors_Nurses') && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">
                    Special ID{' '}
                    {stateData?.fareType[0] == 'Student' && (
                      <span className="text-red-500">*</span>
                    )}
                  </h4>
                  <Input
                    placeholder="ID Number (Student/Senior Citizen/Armed Forces/Medical Professional)"
                    className="outline-none"
                    width={'w-full'}
                    name={`idNumber`}
                    id={`idNumber`}
                    error={paxError[index]?.idNumber}
                    value={formData.idNumber}
                    onInput={(e) => {
                      const currentValue = (e.target as HTMLInputElement).value;
                      setFormData({
                        ...formData,
                        idNumber: currentValue,
                      });
                    }}
                  />
                </div>
              )}

              {travellerDetails[index]?.pax_type === 'INF' && (
                <div className="w-full">
                  <Combobox
                    options={assistedByOptions.filter(
                      (option) =>
                        !travellerDetails.some(
                          (traveller, idx) =>
                            idx !== index &&
                            traveller?.travelWithId === option.id,
                        ),
                    )}
                    value={formData.travelWith}
                    onSelect={(data: any) => {
                      setFormData((prev) => ({
                        ...prev,
                        travelWith: data.value,
                        travelWithId: data.id,
                      }));
                    }}
                    placeholder="Assisted By *"
                  />
                  {paxError[index]?.travelWith && (
                    <span className="mt-1 block text-xs text-red-500">
                      {paxError[index].travelWith}
                    </span>
                  )}
                </div>
              )}

              {stateData.travelMode === 'IN' && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">
                    Passport Details <span className="text-red-500">*</span>
                  </h4>

                  <div className="w-full">
                    <Combobox
                      options={nationalityOptions}
                      value={formData.nationality}
                      onSelect={(data: any) => {
                        setFormData((prev) => ({
                          ...prev,
                          nationality: data.value,
                          nationalityId: data.id,
                        }));
                      }}
                      placeholder="Nationality *"
                    />
                    {paxError[index]?.nationality && (
                      <span className="mt-1 block text-xs text-red-500">
                        {paxError[index].nationality}
                      </span>
                    )}
                  </div>

                  <div className="w-full">
                    <Combobox
                      options={issueCountryOptions}
                      value={formData.issued_country}
                      onSelect={(data: any) => {
                        setFormData((prev) => ({
                          ...prev,
                          issued_country: data.value,
                          issued_countryId: data.id,
                        }));
                      }}
                      placeholder="Issued Country *"
                    />
                    {paxError[index]?.issued_country && (
                      <span className="mt-1 block text-xs text-red-500">
                        {paxError[index].issued_country}
                      </span>
                    )}
                  </div>

                  <Input
                    error={paxError[index]?.passport_no}
                    placeholder="Passport No"
                    name={`passport_no`}
                    id={`passport_no`}
                    value={formData.passport_no}
                    onInput={(e) => {
                      const currentValue = (e.target as HTMLInputElement).value;
                      setFormData({
                        ...formData,
                        passport_no: currentValue,
                      });
                    }}
                    className="w-full focus:outline-none"
                  />

                  <DatePicker
                    title="Passport Issue Date"
                    setDate={(date) => {
                      const formattedDate = formatDate(date, 'dd MMM yyyy');
                      setFormData((prevData) => ({
                        ...prevData,
                        issue_date: formattedDate,
                      }));
                    }}
                    isChangeMonth={true}
                    selectedDate={
                      formData.issue_date
                        ? new Date(formData.issue_date)
                        : new Date()
                    }
                    disableDates={{
                      min: new Date(
                        new Date().setFullYear(new Date().getFullYear() - 15),
                      ),
                      max: new Date(),
                    }}
                    disabled={false}
                    className="w-full"
                  >
                    <TriggerComponent
                      error={paxError[index]?.issue_date}
                      title="Passport Issue Date"
                      value={formData.issue_date || ''}
                      name={`issue_date`}
                      id={`issue_date`}
                    />
                  </DatePicker>

                  <DatePicker
                    isChangeMonth={true}
                    title="Passport Expiry"
                    setDate={(date) => {
                      const formattedDate = formatDate(date, 'dd MMM yyyy');
                      setFormData((prevData) => ({
                        ...prevData,
                        expiry_date: formattedDate,
                      }));
                    }}
                    selectedDate={
                      formData.expiry_date
                        ? new Date(formData.expiry_date)
                        : formData.issue_date
                          ? new Date(
                              new Date(formData.issue_date).setFullYear(
                                new Date(formData.issue_date).getFullYear() +
                                  15,
                              ),
                            )
                          : new Date(
                              new Date().setFullYear(
                                new Date().getFullYear() + 15,
                              ),
                            )
                    }
                    disableDates={{
                      min: formData.issue_date
                        ? new Date(formData.issue_date)
                        : new Date(),
                      max: formData.issue_date
                        ? new Date(
                            new Date(formData.issue_date).setFullYear(
                              new Date(formData.issue_date).getFullYear() + 15,
                            ),
                          )
                        : new Date(
                            new Date().setFullYear(
                              new Date().getFullYear() + 15,
                            ),
                          ),
                    }}
                    disabled={false}
                    className="w-full"
                  >
                    <TriggerComponent
                      error={paxError[index]?.expiry_date}
                      title="Passport Expiry"
                      value={formData.expiry_date || ''}
                      name={`expiry_date`}
                      id={`expiry_date`}
                    />
                  </DatePicker>
                </div>
              )}

              <DialogFooter className="mt-4">
                <Button
                  type="submit"
                  variant="outline"
                  className="hover:bg-primary/90 w-full bg-primary text-white sm:w-auto"
                >
                  Save & Continue
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      );
    },
    (prevProps, nextProps) => {
      // Custom comparison function to determine if component should re-render
      return (
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.index === nextProps.index
      );
    },
  );

  // Add display name for better debugging
  MobilePassengerPopup.displayName = 'MobilePassengerPopup';

  // Also memoize the CompanyDetailsPopup component with proper state initialization
  const CompanyDetailsPopup = React.memo(
    ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
      const [companyFormData, setCompanyFormData] = React.useState({
        register_company_name: '',
        register_no: '',
        register_email: '',
        register_address: '',
        register_mobile: '',
      });

      // Initialize form data when popup opens
      React.useEffect(() => {
        if (isOpen) {
          setCompanyFormData({
            register_company_name: gstDetails.register_company_name || '',
            register_no: gstDetails.register_no || '',
            register_email: gstDetails.register_email || '',
            register_address: gstDetails.register_address || '',
            register_mobile: gstDetails.register_mobile || '',
          });
        }
      }, [isOpen, gstDetails]);

      const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Update the main GST details state
        updateGstDetailsState(companyFormData);
        onClose();
      };

      return (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent
            className="max-h-[90vh] max-w-[95%] overflow-y-auto rounded-lg p-4 sm:hidden"
            aria-describedby="company-form-description"
          >
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                Company Details
              </DialogTitle>
              <DialogDescription
                id="company-form-description"
                className="text-sm text-gray-500"
              >
                Please fill in your company GST details
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-4">
              <Input
                placeholder="Company Name"
                required={true}
                type="text"
                name="register_company_name"
                id="register_company_name"
                value={companyFormData.register_company_name}
                onInput={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    register_company_name: (e.target as HTMLInputElement).value,
                  })
                }
                className="w-full focus:outline-none"
              />

              <Input
                placeholder="Registration No."
                required={true}
                type="text"
                name="register_no"
                id="register_no"
                value={companyFormData.register_no}
                onInput={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    register_no: (e.target as HTMLInputElement).value,
                  })
                }
                className="w-full focus:outline-none"
              />

              <Input
                placeholder="Registration Email"
                required={true}
                type="email"
                name="register_email"
                id="register_email"
                value={companyFormData.register_email}
                onInput={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    register_email: (e.target as HTMLInputElement).value,
                  })
                }
                className="w-full focus:outline-none"
              />

              <Input
                placeholder="Registration Address"
                required={true}
                type="text"
                name="register_address"
                id="register_address"
                value={companyFormData.register_address}
                onInput={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    register_address: (e.target as HTMLInputElement).value,
                  })
                }
                className="w-full focus:outline-none"
              />

              <Input
                placeholder="Registration Mobile"
                required={true}
                type="tel"
                name="register_mobile"
                id="register_mobile"
                value={companyFormData.register_mobile}
                onInput={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    register_mobile: (e.target as HTMLInputElement).value,
                  })
                }
                className="w-full focus:outline-none"
              />

              <DialogFooter className="mt-4">
                <Button
                  type="submit"
                  variant="outline"
                  className="hover:bg-primary/90 w-full bg-primary text-white sm:w-auto"
                >
                  Save Details
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      );
    },
    (prevProps, nextProps) => {
      return prevProps.isOpen === nextProps.isOpen;
    },
  );

  CompanyDetailsPopup.displayName = 'CompanyDetailsPopup';
  return (
    <div className="bg-tertiary">
      <div className="h-14 w-full bg-primary sm:h-20 lg:h-48" />
      <div className="absolute inset-x-0 top-14 mx-auto px-1 pb-4 sm:top-14 sm:w-[1200px] sm:pb-8 lg:top-32 lg:pb-20">
        <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
          <h2 className="pl-2 text-2xs font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
            Traveller Details
          </h2>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="w-full rounded-lg bg-white p-3 shadow-lg sm:rounded-xl sm:p-4 lg:basis-[75%] lg:p-6">
            <div className="mt-0 w-full transform rounded-xl sm:mt-3 sm:rounded-2xl md:mt-4 lg:mt-6">
              <div className="flex w-full flex-1 flex-col">
                <form action="travelerForm">
                  <div
                    className={`w-full rounded bg-sky-50 px-2 py-2 text-2xs sm:text-xs`}
                  >
                    <span className="rounded bg-blue-300 px-1 py-0.5 text-2xs text-cyan-700 sm:text-xs">
                      NOTE :{''}
                    </span>
                    <span className="ml-1 break-words text-2xs text-zinc-800 sm:text-xs">
                      Please ensure that you enter the name exactly as it
                      appears on your government-issued photo ID.
                    </span>
                  </div>

                  <h3 className="my-0 hidden text-base font-medium text-zinc-800 sm:my-4 sm:block">
                    Passenger Details
                  </h3>
                  {travellerDetails.map((traveller, index) => (
                    <div
                      key={`traveller-${traveller.pax_type}`}
                      className="my-2 sm:my-4"
                    >
                      {traveller.type !== 'Adult 1' && (
                        <>
                          <div className="flex flex-col gap-2">
                            {traveller.type}
                          </div>
                        </>
                      )}
                      {/* Desktop view */}
                      <div className="mb-4 hidden gap-3 sm:grid sm:grid-cols-12">
                        <div className="col-span-12 sm:col-span-2">
                          <Input
                            ref={inputRefs.current[index]?.title}
                            value={
                              traveller.title ||
                              (travellerDetails[index]?.pax_type == 'ADT'
                                ? 'Mr'
                                : 'Mstr')
                            }
                            options={
                              travellerDetails[index]?.pax_type == 'ADT'
                                ? ['Mr', 'Ms', 'Mrs']
                                : ['Mstr', 'Miss']
                            }
                            placeholder="Title"
                            className="outline-none"
                            required={true}
                            width={'w-full'}
                            name={`title`}
                            id={`title`}
                            onInput={(e) => {
                              updateTravellerDetails(
                                (e.target as HTMLInputElement).value,
                                'title',
                                index,
                              );
                            }}
                            onKeyDown={(e) => handleKeyDown(e, 'title', index)}
                          />
                        </div>

                        <div className="col-span-12 sm:col-span-5">
                          <Input
                            ref={inputRefs.current[index]?.firstName}
                            placeholder="First Name"
                            className="outline-none"
                            error={errors[index]?.firstName}
                            required={true}
                            width={'w-full sm:w-full md:w-full'}
                            name={`firstName`}
                            id={`firstName`}
                            value={traveller.firstName.toUpperCase()}
                            onInput={(e) => {
                              updateTravellerDetails(
                                (e.target as HTMLInputElement).value,
                                'firstName',
                                index,
                              );
                            }}
                            onKeyDown={(e) =>
                              handleKeyDown(e, 'firstName', index)
                            }
                          />
                        </div>

                        <div className="col-span-12 sm:col-span-5">
                          <Input
                            ref={inputRefs.current[index]?.lastName}
                            placeholder="Last Name"
                            className="outline-none"
                            required={true}
                            width={'w-full sm:w-full md:w-full'}
                            name={`lastName`}
                            id={`lastName`}
                            value={traveller.lastName.toUpperCase()}
                            error={errors[index]?.lastName}
                            onInput={(e) => {
                              updateTravellerDetails(
                                (e.target as HTMLInputElement).value,
                                'lastName',
                                index,
                              );
                            }}
                            onKeyDown={(e) =>
                              handleKeyDown(e, 'lastName', index)
                            }
                          />
                        </div>
                        {(travellerDetails[index]?.pax_type != 'ADT' ||
                          flightSearch?.travelMode == 'IN' ||
                          stateData?.fareType[0] == 'Senior_Citizen' ||
                          stateData?.fareType[0] == 'Student') && (
                          <div className="col-span-12 sm:col-span-3">
                            <DatePicker
                              title="Date of Birth"
                              isChangeMonth={true}
                              setDate={(date) => {
                                onChangeDate(date, index, 'dob');
                              }}
                              selectedDate={
                                traveller.dob
                                  ? new Date(traveller.dob)
                                  : new Date(
                                      traveller.pax_type == 'ADT'
                                        ? subYears(
                                            new Date(flightSearchData.date),
                                            12,
                                          )
                                        : traveller.pax_type == 'CHD'
                                          ? subYears(
                                              new Date(flightSearchData.date),
                                              2,
                                            )
                                          : new Date(flightSearchData.date),
                                    )
                              }
                              disableDates={{
                                min:
                                  traveller.pax_type == 'ADT'
                                    ? subYears(
                                        new Date(flightSearchData.date),
                                        100,
                                      )
                                    : traveller.pax_type == 'CHD'
                                      ? subYears(
                                          new Date(flightSearchData.date),
                                          12,
                                        )
                                      : subYears(
                                          new Date(flightSearchData.date),
                                          2,
                                        ),
                                max:
                                  traveller.pax_type == 'ADT'
                                    ? subYears(
                                        new Date(flightSearchData.date),
                                        12,
                                      )
                                    : traveller.pax_type == 'CHD'
                                      ? subYears(
                                          new Date(flightSearchData.date),
                                          2,
                                        )
                                      : new Date(flightSearchData.date),
                              }}
                              disabled={false}
                            >
                              <TriggerComponent
                                ref={inputRefs.current[index]?.dob}
                                error={errors[index]?.dob}
                                onKeyDown={(e) =>
                                  handleKeyDown(e, 'dob', index)
                                }
                                title="Date of Birth"
                                value={traveller.dob}
                                name={`dob`}
                                id={`dob`}
                              />
                            </DatePicker>
                          </div>
                        )}
                        {(stateData?.fareType[0] == 'Student' ||
                          stateData?.fareType[0] == 'Senior_Citizen' ||
                          stateData?.fareType[0] == 'Armed_Forces' ||
                          stateData?.fareType[0] == 'Doctors_Nurses') && (
                          <div className="col-span-12 sm:col-span-3">
                            <Input
                              ref={inputRefs.current[index]?.idNumber}
                              placeholder="ID Number"
                              className="outline-none"
                              width={'w-full'}
                              name={`idNumber`}
                              id={`idNumber`}
                              value={travellerDetails[index]?.idNumber || ''}
                              onInput={(e) => {
                                updateTravellerDetails(
                                  (e.target as HTMLInputElement).value,
                                  'idNumber',
                                  index,
                                );
                              }}
                              onKeyDown={(e) =>
                                handleKeyDown(e, 'idNumber', index)
                              }
                            />
                          </div>
                        )}
                        {traveller.pax_type === 'INF' && (
                          <div className="col-span-12 sm:col-span-4">
                            <Combobox
                              options={assistedByOptions}
                              ref={inputRefs.current[index]?.travelWith}
                              value={traveller.travelWith}
                              error={errors[index]?.travelWith}
                              onSelect={(data: any) => {
                                updateTravellerDetails(
                                  data.value,
                                  'travelWith',
                                  index,
                                  'dropdown',
                                  data.id,
                                );
                              }}
                              placeholder="Assisted By"
                            />
                          </div>
                        )}
                      </div>

                      {/* Mobile view - card with summary */}
                      <div className="mb-4 rounded-lg border border-gray-100 bg-white px-3 py-3 shadow-sm sm:hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium">
                            {traveller.type || `Passenger ${index + 1}`}
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCurrentPassengerIndex(index);
                              setShowMobilePopup(true);
                            }}
                            className="hover:text-primary/90 px-2 py-1 text-primary"
                          >
                            {traveller.firstName || traveller.lastName
                              ? 'Edit'
                              : 'Add Details'}
                          </Button>
                        </div>

                        {(traveller.firstName || traveller.lastName) && (
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p className="flex justify-between">
                              <span className="text-gray-500">Name:</span>
                              <span>
                                {`${traveller.title || ''} ${traveller.firstName || ''} ${traveller.lastName || ''}`.trim()}
                              </span>
                            </p>
                            {traveller.dob && (
                              <p className="flex justify-between">
                                <span className="text-gray-500">DOB:</span>
                                <span>{traveller.dob}</span>
                              </p>
                            )}
                            {traveller.pax_type === 'INF' &&
                              traveller.travelWith && (
                                <p className="flex justify-between">
                                  <span className="text-gray-500">
                                    Assisted By:
                                  </span>
                                  <span>{traveller.travelWith}</span>
                                </p>
                              )}
                            {stateData.travelMode === 'IN' &&
                              traveller.passport_no && (
                                <p className="flex justify-between">
                                  <span className="text-gray-500">
                                    Passport:
                                  </span>
                                  <span>{traveller.passport_no}</span>
                                </p>
                              )}
                          </div>
                        )}
                      </div>

                      {/* Passport details section - only show in desktop view */}
                      {stateData.travelMode === 'IN' && (
                        <div className="hidden sm:block">
                          {/* <h3 className="my-4 text-base font-medium text-zinc-800">
                            Passport Details
                          </h3> */}
                          <div className="mb-6 grid grid-cols-12 gap-3">
                            <div className="col-span-12 sm:col-span-3">
                              <Combobox
                                options={nationalityOptions}
                                ref={inputRefs.current[index]?.nationalityId}
                                value={traveller.nationality}
                                error={errors[index]?.nationality}
                                onSelect={(data: any) => {
                                  updateTravellerDetails(
                                    data.value,
                                    'nationality',
                                    index,
                                    'dropdown',
                                    data.id,
                                  );
                                }}
                                placeholder="Nationality"
                              />
                            </div>
                            <div className="col-span-12 sm:col-span-3">
                              <Input
                                error={errors[index]?.passport_no}
                                ref={inputRefs.current[index]?.passport_no}
                                placeholder="Passport No"
                                onInput={(e) => {
                                  updateTravellerDetails(
                                    (e.target as HTMLInputElement).value,
                                    'passport_no',
                                    index,
                                  );
                                }}
                                className="max-w-72 focus:outline-none"
                                value={traveller.passport_no}
                                onKeyDown={(e) =>
                                  handleKeyDown(e, 'passport_no', index)
                                }
                              />
                            </div>

                            <div className="col-span-12 sm:col-span-3">
                              <Combobox
                                options={issueCountryOptions}
                                ref={inputRefs.current[index]?.issued_countryId}
                                value={traveller.issued_country}
                                error={errors[index]?.issued_country}
                                onSelect={(data: any) => {
                                  updateTravellerDetails(
                                    data.value,
                                    'issued_country',
                                    index,
                                    'dropdown',
                                    data.id,
                                  );
                                }}
                                placeholder="Issued Country"
                              />
                            </div>
                            <div className="col-span-12 sm:col-span-3">
                              <DatePicker
                                title="Passport Issue Date"
                                setDate={(date) => {
                                  onChangeDate(date, index, 'issue_date');
                                }}
                                isChangeMonth={true}
                                selectedDate={
                                  traveller.issue_date
                                    ? new Date(traveller.issue_date)
                                    : new Date()
                                }
                                disableDates={{
                                  min: new Date(
                                    new Date().setFullYear(
                                      new Date().getFullYear() - 15,
                                    ),
                                  ),
                                  max: new Date(),
                                }}
                                disabled={false}
                              >
                                <TriggerComponent
                                  ref={inputRefs.current[index]?.issue_date}
                                  error={errors[index]?.issue_date}
                                  onKeyDown={(e) =>
                                    handleKeyDown(e, 'issue_date', index)
                                  }
                                  title="Passport Issue Date"
                                  value={traveller.issue_date || ''}
                                  name={`issue_date`}
                                  id={`issue_date`}
                                />
                              </DatePicker>
                            </div>

                            <div className="col-span-12 sm:col-span-3">
                              <DatePicker
                                isChangeMonth={true}
                                title="Passport Expiry"
                                setDate={(date) => {
                                  onChangeDate(date, index, 'expiry_date');
                                }}
                                selectedDate={
                                  traveller.expiry_date
                                    ? new Date(traveller.expiry_date)
                                    : traveller.issue_date
                                      ? new Date(
                                          new Date(
                                            traveller.issue_date,
                                          ).setFullYear(
                                            new Date(
                                              traveller.issue_date,
                                            ).getFullYear() + 15,
                                          ),
                                        )
                                      : new Date(
                                          new Date().setFullYear(
                                            new Date().getFullYear() + 15,
                                          ),
                                        )
                                }
                                disableDates={{
                                  min: traveller.issue_date
                                    ? new Date(traveller.issue_date)
                                    : new Date(),
                                  max: traveller.issue_date
                                    ? new Date(
                                        new Date(
                                          traveller.issue_date,
                                        ).setFullYear(
                                          new Date(
                                            traveller.issue_date,
                                          ).getFullYear() + 15,
                                        ),
                                      )
                                    : new Date(
                                        new Date().setFullYear(
                                          new Date().getFullYear() + 15,
                                        ),
                                      ),
                                }}
                                disabled={false}
                              >
                                <TriggerComponent
                                  ref={inputRefs.current[index]?.expiry_date}
                                  error={errors[index]?.expiry_date}
                                  onKeyDown={(e) =>
                                    handleKeyDown(e, 'expiry_date', index)
                                  }
                                  title="Passport Expiry"
                                  value={traveller.expiry_date || ''}
                                  name={`expiry_date`}
                                  id={`expiry_date`}
                                />
                              </DatePicker>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  <h3 className="my-4 text-base font-medium text-zinc-800">
                    Contact Details
                  </h3>
                  <div className="my-3 text-xs">
                    Your ticket will be sent to this Mobile Number and Email Id
                  </div>

                  <div className="mb-6 grid grid-cols-12 gap-3">
                    <div className="col-span-12 sm:col-span-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="border-light h-10 w-full justify-between rounded-l-md border bg-background px-3 py-2 text-sm font-normal"
                          >
                            <div className="flex items-center gap-2">
                              <span>{selectedCountry.flag}</span>
                              <span>{selectedCountry.code}</span>
                            </div>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="left-0 max-h-[300px] w-[300px] overflow-y-auto">
                          <div className="sticky left-0 top-0 z-10 bg-white px-3 py-2">
                            <div className="flex items-center rounded-md border px-2">
                              <Search className="h-4 w-4 text-gray-500" />
                              <Input
                                placeholder="Search countries..."
                                className="border-0 focus-visible:ring-0 bg-white focus-visible:ring-offset-0"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                              />
                            </div>
                          </div>
                          <DropdownMenuSeparator />
                          <DropdownMenuGroup>
                            <DropdownMenuLabel className="px-3 text-xs font-medium text-gray-500">
                              Popular Countries
                            </DropdownMenuLabel>
                            {/* Popular countries section */}
                            {[
                              countries.find((c) => c.code === '+91'), // India
                              // countries.find((c) => c.code === '+1'), // USA
                              // countries.find((c) => c.code === '+44'), // UK
                              // countries.find((c) => c.code === '+971'), // UAE
                            ].map(
                              (country) =>
                                country && (
                                  <DropdownMenuItem
                                    key={country.code}
                                    className="flex cursor-pointer items-center gap-2 px-3 py-2"
                                    onClick={() => {
                                      setSelectedCountry({
                                        code: country.code,
                                        flag: country.flag,
                                        name: country.name,
                                      });
                                    }}
                                  >
                                    <span className="mr-2 text-lg">
                                      {country.flag}
                                    </span>
                                    <span className="flex-1">
                                      {country.name}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {country.code}
                                    </span>
                                  </DropdownMenuItem>
                                ),
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-text-primary px-3 text-xs font-medium">
                              All Countries
                            </DropdownMenuLabel>
                            {/* All countries section */}
                            {filteredCountries.map((country) => (
                              <DropdownMenuItem
                                key={country.code + country.name}
                                className="flex cursor-pointer items-center gap-2 px-3 py-2"
                                onClick={() => {
                                  setSelectedCountry({
                                    code: country.code,
                                    flag: country.flag,
                                    name: country.name,
                                  });
                                }}
                              >
                                <span className="mr-2 text-lg">
                                  {country.flag}
                                </span>
                                <span className="flex-1">{country.name}</span>
                                <span className="text-sm text-gray-500">
                                  {country.code}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="col-span-12 sm:col-span-5">
                      <Input
                        ref={inputRefs.current[0]?.mobile_no}
                        placeholder="Mobile Number"
                        type="text"
                        error={errors[0]?.mobile_no}
                        maxLength={10}
                        value={contactDetails.mobile_no}
                        onInput={(e) => {
                          const value = (e.target as HTMLInputElement).value;
                          // Only allow numbers
                          if (/^\d*$/.test(value)) {
                            updateContactDetails(value, 'mobile_no');
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, 'mobile_no')}
                        className={`border-light h-10 w-full rounded-r-md border bg-background px-3 py-2 text-sm focus:outline-none ${
                          errors[0]?.mobile_no
                            ? 'border-red-500'
                            : 'border-light'
                        }`}
                      />
                    </div>

                    <div className="col-span-12 sm:col-span-5">
                      <Input
                        ref={inputRefs.current[0]?.email_id}
                        placeholder="Email Id"
                        type="text"
                        error={errors[0]?.email_id}
                        value={contactDetails.email_id}
                        onInput={(e) => {
                          updateContactDetails(
                            (e.target as HTMLInputElement).value,
                            'email_id',
                          );
                        }}
                        onKeyDown={(e) => handleKeyDown(e, 'email_id')}
                        className={`border-light h-10 w-full rounded-r-md border bg-background px-3 py-2 text-sm focus:outline-none ${
                          errors[0]?.email_id
                            ? 'border-red-500'
                            : 'border-light'
                        }`}
                      />
                    </div>
                  </div>

                  {/* GST Details section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <h3 className="my-4 text-base font-medium text-zinc-800">
                        GST Details
                      </h3>
                      {/* Mobile-only button to open GST details popup */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCompanyPopup(true)}
                        className="hover:text-primary/90 text-primary sm:hidden"
                      >
                        {gstDetails.register_company_name
                          ? 'Edit'
                          : 'Add GST Details'}
                      </Button>
                    </div>

                    {/* Mobile summary of GST details */}
                    <div className="mb-4 sm:hidden">
                      {gstDetails.register_company_name && (
                        <div className="rounded-lg border border-gray-100 bg-white px-3 py-3 shadow-sm">
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="flex justify-between">
                              <span className="text-gray-500">Company:</span>
                              <span>{gstDetails.register_company_name}</span>
                            </p>
                            {gstDetails.register_no && (
                              <p className="flex justify-between">
                                <span className="text-gray-500">GST No:</span>
                                <span>{gstDetails.register_no}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Desktop GST details form */}
                    <div className="hidden gap-4 sm:grid sm:grid-cols-12">
                      <div className="col-span-12 sm:col-span-4">
                        <Input
                          ref={inputRefs.current[0]?.register_company_name}
                          placeholder="Company Name"
                          required={true}
                          type="text"
                          name="register_company_name"
                          id="register_company_name"
                          value={gstDetails.register_company_name}
                          error={errors[0]?.register_company_name}
                          onInput={(e) =>
                            updateGstDetailsState({
                              ...gstDetails,
                              register_company_name: (
                                e.target as HTMLInputElement
                              ).value,
                            })
                          }
                          onKeyDown={(e) =>
                            handleKeyDown(e, 'register_company_name')
                          }
                          className="focus:outline-none"
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-4">
                        <Input
                          ref={inputRefs.current[0]?.register_no}
                          placeholder="Registration No."
                          required={true}
                          type="text"
                          name="register_no"
                          id="register_no"
                          value={gstDetails.register_no}
                          onInput={(e) => {
                            const value = (
                              e.target as HTMLInputElement
                            ).value.toUpperCase();
                            updateGstDetailsState({
                              ...gstDetails,
                              register_no: value,
                            });
                          }}
                          onKeyDown={(e) => handleKeyDown(e, 'register_no')}
                          className={`focus:outline-none ${
                            errors[0]?.register_no ? 'border-red-500' : ''
                          }`}
                          error={errors[0]?.register_no}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-4">
                        <Input
                          ref={inputRefs.current[0]?.register_email}
                          placeholder="Registration Email"
                          required={true}
                          type="email"
                          name="register_email"
                          id="register_email"
                          value={gstDetails.register_email}
                          onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value;
                            updateGstDetailsState({
                              ...gstDetails,
                              register_email: value,
                            });
                          }}
                          onKeyDown={(e) => handleKeyDown(e, 'register_email')}
                          className={`focus:outline-none ${
                            errors[0]?.register_email ? 'border-red-500' : ''
                          }`}
                          error={errors[0]?.register_email}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-4">
                        <Input
                          ref={inputRefs.current[0]?.register_address}
                          placeholder="Registration Address"
                          required={true}
                          type="text"
                          name="register_address"
                          id="register_address"
                          value={gstDetails.register_address}
                          error={errors[0]?.register_address}
                          onInput={(e) =>
                            updateGstDetailsState({
                              ...gstDetails,
                              register_address: (e.target as HTMLInputElement)
                                .value,
                            })
                          }
                          onKeyDown={(e) =>
                            handleKeyDown(e, 'register_address')
                          }
                          className="focus:outline-none"
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-4">
                        <Input
                          ref={inputRefs.current[0]?.register_mobile}
                          placeholder="Registration Mobile"
                          required={true}
                          type="tel"
                          name="register_mobile"
                          id="register_mobile"
                          value={gstDetails.register_mobile}
                          onInput={(e) => {
                            const value = (e.target as HTMLInputElement).value;
                            // Only allow numbers for mobile
                            if (/^\d*$/.test(value)) {
                              updateGstDetailsState({
                                ...gstDetails,
                                register_mobile: value,
                              });
                            }
                          }}
                          onKeyDown={(e) => handleKeyDown(e, 'register_mobile')}
                          className={`focus:outline-none ${
                            errors[0]?.register_mobile ? 'border-red-500' : ''
                          }`}
                          error={errors[0]?.register_mobile}
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="flex basis-full flex-col gap-5 lg:basis-[25%]">
            <div>
              <FareSummary
                summary={selectedBucket}
                callback={callback}
                isLoader={isLoading}
              />
            </div>
            <div>
              <BookingSummary />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Popups */}
      <MobilePassengerPopup
        isOpen={showMobilePopup}
        onClose={() => setShowMobilePopup(false)}
        index={currentPassengerIndex}
      />

      <CompanyDetailsPopup
        isOpen={showCompanyPopup}
        onClose={() => setShowCompanyPopup(false)}
      />
    </div>
  );
}

export default TravelerDetails;

// Update the TriggerComponent to accept ref and onKeyDown
const TriggerComponent = React.forwardRef(
  (
    {
      title,
      value,
      minDate,
      maxDate,
      onKeyDown,
      error,
      name,
      id,
    }: {
      title: string;
      value: Date | string;
      minDate?: Date;
      maxDate?: Date;
      onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
      error?: string;
      name?: string;
      id?: string;
    },
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const formatDateString = (date: Date) => {
      if (!isValid(date)) return '';
      return formatDate(date, 'dd MMM yyyy');
    };

    return (
      <div className="w-full sm:w-52">
        <Input
          ref={ref}
          readOnly
          name={name}
          id={id}
          className={`placeholder:text-muted-foreground focus-visible:ring-ring border-light flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${error ? 'border-red-500' : 'border-light'}`}
          placeholder={title}
          value={
            (title === 'Date of Birth' || title === 'Passport Expiry') &&
            value instanceof Date &&
            isValid(value)
              ? formatDateString(value)
              : value?.toString() || ''
          }
          max={maxDate ? maxDate.toISOString().split('T')[0] : undefined}
          min={minDate ? minDate.toISOString().split('T')[0] : undefined}
          onKeyDown={onKeyDown}
        />
        {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
      </div>
    );
  },
);

TriggerComponent.displayName = 'TriggerComponent';
